import React from 'react';
import { motion } from 'framer-motion';
import { Check, Minus } from 'lucide-react';
import SEO from '../components/SEO';

const PriceSection = ({ title, items }) => (
  <div className="mb-16">
    <h3 className="text-2xl font-serif text-brand-950 mb-8 flex items-center">
      <span className="w-8 h-px bg-brand-200 mr-4"></span>
      {title}
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
      {items.map((item, index) => (
        <div key={index} className="flex justify-between items-end border-b border-brand-50 pb-4 group hover:border-brand-200 transition-colors">
          <div className="pr-4">
            <span className="text-brand-950 font-medium block mb-1">{item.name}</span>
            {item.description && <span className="text-brand-500 text-xs font-light italic">{item.description}</span>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function Prices() {
  const categories = [
    {
      title: "Chokolade & Specialiteter",
      items: [
        { name: "Aalborg Chokolade", description: "Mørk chokolade med nødder og unikt print. Kun på bestilling til firmaer." },
        { name: "Gavekurve", description: "Sammensat efter ønske til erhverv og forhandlere." },
      ]
    }
  ];

  const pricesSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Hvordan foregår levering?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Jeg sender med GLS til hele landet. Leveringstiden er typisk 2-4 hverdage for lagervarer. Fragtfri levering ved køb over 500 kr."
        }
      },
      {
        "@type": "Question",
        "name": "Hvilke betalingsmetoder accepteres?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Jeg modtager MobilePay og bankoverførsel. Erhvervskunder kan faktureres efter aftale."
        }
      },
      {
        "@type": "Question",
        "name": "Hvordan fungerer specialordrer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ved specialbestillinger opkræves et depositum på 50% ved opstart. Det resterende beløb betales ved levering."
        }
      }
    ]
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO 
        title="Priser & Forhandlerinformation"
        description="Er du forhandler eller erhvervskunde? Få information om priser, reoler til låns og vilkår for Cards by Eve håndtegnede kort, chokolade og specialiteter."
        schema={pricesSchema}
      />
      {/* Hero */}
      <section className="bg-brand-50 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center space-x-4 mb-8">
              <Minus className="w-8 h-8 text-brand-950" />
              <span className="text-brand-950 text-xs uppercase tracking-[0.4em] font-medium">
                Forhandlerinfo
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-serif text-brand-950 leading-tight mb-8">
              Information til <br />
              <span className="italic text-brand-600">forhandlere</span>
            </h1>
            <div className="text-brand-800 text-lg font-light leading-relaxed max-w-2xl space-y-4">
              <p>Kære forhandler,</p>
              <p>
                Kontakt mig endelig for at få mine aktuelle priser.
              </p>
              <p>
                Reolen er altid til låns uden beregning, så længe man har lyst til at forhandle mine kort.
              </p>
              <p>
                Ring eller skriv ved spørgsmål eller for at få tilsendt en fuld prisliste.
              </p>
              <p className="pt-4">
                De bedste hilsener,<br />
                Evelyn
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Price List */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl">
            {categories.map((cat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <PriceSection title={cat.title} items={cat.items} />
              </motion.div>
            ))}
          </div>

          {/* Info Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 bg-brand-950 text-white p-8 md:p-12 rounded-sm flex flex-col md:flex-row items-center justify-between gap-8"
          >
            <div className="max-w-xl">
              <h4 className="text-2xl font-serif mb-4">Har du en speciel forespørgsel?</h4>
              <p className="text-brand-200 font-light">
                Jeg elsker at arbejde på unikke projekter. Hvis du har en speciel forespørgsel, så tøv ikke med at række ud.
              </p>
            </div>
            <a
              href="/kontakt"
              className="whitespace-nowrap bg-white text-brand-950 px-10 py-4 text-xs uppercase tracking-widest font-bold hover:bg-brand-50 transition-colors"
            >
              Kontakt mig for tilbud
            </a>
          </motion.div>
        </div>
      </section>

      {/* FAQ/Terms */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <h4 className="text-brand-950 font-serif text-xl mb-4">Levering</h4>
              <p className="text-brand-600 text-sm font-light leading-relaxed">
                Jeg sender med GLS til hele landet. Leveringstiden er typisk 2-4 hverdage for lagervarer. Fragtfri levering ved køb over 500 kr.
              </p>
            </div>
            <div>
              <h4 className="text-brand-950 font-serif text-xl mb-4">Betaling</h4>
              <p className="text-brand-600 text-sm font-light leading-relaxed">
                Jeg modtager MobilePay og bankoverførsel. Erhvervskunder kan faktureres efter aftale.
              </p>
            </div>
            <div>
              <h4 className="text-brand-950 font-serif text-xl mb-4">Specialordrer</h4>
              <p className="text-brand-600 text-sm font-light leading-relaxed">
                Ved specialbestillinger opkræves et depositum på 50% ved opstart. Det resterende beløb betales ved levering.
              </p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
